"use client"

export {
  getPageScrollElement,
  bindVerticalScrollListeners as bindPageScrollListeners,
} from "@exxatdesignux/ui/lib/page-scroll-port"

import * as React from "react"
import { getPageScrollElement } from "@exxatdesignux/ui/lib/page-scroll-port"

const COMPOSER_BUILDER_SCROLL_ATTR = "data-composer-builder-scroll"

function isVisibleScroller(el: HTMLElement): boolean {
  if (el.getClientRects().length === 0) return false
  const style = getComputedStyle(el)
  if (style.display === "none" || style.visibility === "hidden") return false
  return el.clientHeight > 0
}

function readScrollStuck(): boolean {
  if (typeof window !== "undefined" && window.scrollY > 0) return true

  const scrollRoot = getPageScrollElement()
  if ((scrollRoot?.scrollTop ?? 0) > 0) return true

  const composerScrollers = document.querySelectorAll<HTMLElement>(
    `[${COMPOSER_BUILDER_SCROLL_ATTR}]`,
  )
  return Array.from(composerScrollers).some(
    (el) => isVisibleScroller(el) && el.scrollTop > 0,
  )
}

type ScrollStuckListener = () => void

let stuckSnapshot = false
const listeners = new Set<ScrollStuckListener>()
const boundComposerScrollers = new WeakSet<HTMLElement>()
let boundPageScroll: HTMLElement | null = null
let observer: MutationObserver | null = null
let bound = false

function emitIfChanged() {
  const next = readScrollStuck()
  if (next === stuckSnapshot) return
  stuckSnapshot = next
  for (const listener of listeners) listener()
}

function bindPageScrollRoot() {
  const root = getPageScrollElement()
  if (!root || root === boundPageScroll) return
  boundPageScroll?.removeEventListener("scroll", emitIfChanged)
  boundPageScroll = root
  root.addEventListener("scroll", emitIfChanged, { passive: true })
}

function bindComposerScroller(el: HTMLElement) {
  if (boundComposerScrollers.has(el)) return
  boundComposerScrollers.add(el)
  el.addEventListener("scroll", emitIfChanged, { passive: true })
}

function scanScrollRoots() {
  if (typeof document === "undefined") return
  bindPageScrollRoot()
  for (const el of document.querySelectorAll<HTMLElement>(
    `[${COMPOSER_BUILDER_SCROLL_ATTR}]`,
  )) {
    bindComposerScroller(el)
  }
}

function ensureGlobalListeners() {
  if (bound || typeof window === "undefined") return
  bound = true
  stuckSnapshot = readScrollStuck()

  window.addEventListener("scroll", emitIfChanged, { passive: true })
  window.addEventListener("resize", emitIfChanged, { passive: true })

  scanScrollRoots()

  if (typeof MutationObserver !== "undefined" && document.body) {
    observer = new MutationObserver(() => {
      scanScrollRoots()
      emitIfChanged()
    })
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "hidden"],
    })
  }
}

function subscribe(listener: ScrollStuckListener): () => void {
  ensureGlobalListeners()
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): boolean {
  ensureGlobalListeners()
  stuckSnapshot = readScrollStuck()
  return stuckSnapshot
}

/** True when the page scrollport (or compose builder column) has scrolled. */
export function useScrollStuck(): boolean {
  return React.useSyncExternalStore(subscribe, getSnapshot, () => false)
}
